import {api2Endpoint} from 'app/config/apiConfig'

import {
  CProject,
  CSubscription,
  CUser,
  IProject,
  ISubscription,
  IUser,
} from 'app/modules/discovery/core'
import {queryDefaults} from 'app/react-query'
import {useMutation, useQuery} from 'react-query'
import {JsonObject, isJsonData, jsonDataIsObjWithId} from 'utilities/types'
import {ParentContext} from './ApiParentContext'
import {ApiRepo} from './ApiRepo'
import {getData} from './api/useGetData'
import {postData} from './api/usePostData'
import {announceReturn, announceSend} from './api/utils/announce'
import errorNotify from './api/utils/errorNotify'

export const ProjectRepo = new ApiRepo<IProject, typeof CProject>({
  name: 'projects',
  recordClass: CProject,
})

//easy to remove
export const SubscriptionRepo = new ApiRepo<ISubscription, typeof CSubscription>({
  name: 'subscriptions',
  recordClass: CSubscription,
})

class CUserRepo extends ApiRepo<IUser, typeof CUser> {
  useSelf(opts?: {children?: string[]}) {
    this.doRegister()

    const selfQuery = useQuery({
      queryKey: ['user-self'],
      queryFn: async () => {
        try {
          this.timestamp = Date.now()
          const route = this.route({id: 'self'})
          announceSend('GET', route)
          const response = await getData<IUser>(api2Endpoint(route))
          announceReturn('GET', route)
          if (!isJsonData(response) || !jsonDataIsObjWithId(response)) {
            throw new Error(
              `Expected JSON-serializable object with id, got: ${JSON.stringify(response)}`
            )
          }
          this._relations.load(response)
          return response.id
        } catch (err) {
          errorNotify(err, `Failed to retrieve user data.`)
        } finally {
          this.timestamp = Date.now()
        }
      },
      cacheTime: queryDefaults.cacheTime,
      staleTime: queryDefaults.staleTime,
    })
    const output = this.useRead(selfQuery.data, {
      children: ['subscriptions', ...(opts?.children ?? [])],
      disabled: !selfQuery.data,
    })
    return output
  }

  // special method uses the old API route (which is exempted from Cognito auth)
  useSignUp(opts?: {parentContext?: ParentContext}) {
    this.doRegister()
    const baseCreate = this._useCreate({parentContext: opts?.parentContext})
    return useMutation(
      async (args: {
        data: IUser & JsonObject
        overrideRoute?: string
        oldApi?: boolean
        parentContext?: ParentContext
      }) => {
        const {data} = args
        baseCreate.mutateAsync({
          data,
          parentContext: args.parentContext,
          overrideRoute: args.overrideRoute ? args.overrideRoute : 'users',
          oldApi: args.oldApi,
          noReturn: true,
        })
        return null
      }
    )
  }

  useResendPasswordEmail(opts?: {parentContext?: ParentContext}) {
    this.doRegister()
    return useMutation(async (id: string) => {
      const route = `${this.route({id})}/resend_initial_password`
      announceSend('POST', route)
      try {
        await postData(api2Endpoint(route), undefined)
      } catch (err) {
        errorNotify(err, `Failed to send password reset.`)
      }
      announceReturn('POST', route)
    })
  }
}

export const UserRepo = new CUserRepo({
  name: 'users',
  recordClass: CUser,
})
